import { useState, useEffect } from "react";
import { 
  Eye, 
  PencilSimple, 
  FloppyDisk, 
  ChartLine, 
  UserPlus, 
  Sparkle,
  SquaresFour,
  Briefcase,
  AddressBook,
  Calendar,
  Sliders,
  Info,
  ArrowsClockwise,
  Trophy,
  CurrencyDollar,
  IdentificationCard,
  CaretDown,
  Book,
  FileText,
  HandsClapping
} from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { FightCardEditor } from "@/components/FightCardEditor";
import { FightCardDisplay } from "@/components/FightCardDisplay";
import { BoxerRegistration } from "@/components/BoxerRegistration";
import { BoxerLeaderboard } from "@/components/BoxerLeaderboard";
import { BoxerProfile } from "@/components/BoxerProfile";
import { FightCardGenerator } from "@/components/FightCardGenerator";
import { SponsorRegistration } from "@/components/SponsorRegistration";
import { SponsorList } from "@/components/SponsorList";
import { SponsorProfile } from "@/components/SponsorProfile";
import { BoxerDirectory } from "@/components/BoxerDirectory";
import { UpcomingFights } from "@/components/UpcomingFights";
import { FightResultsManager } from "@/components/FightResultsManager";
import { RankingFAQ } from "@/components/RankingFAQ";
import { RankingSettingsComponent } from "@/components/RankingSettings";
import { SeasonReset } from "@/components/SeasonReset";
import { TournamentBracket } from "@/components/TournamentBracket";
import { BettingManager } from "@/components/BettingManager";
import { FighterOddsDisplay } from "@/components/FighterOddsDisplay";
import { LicenseManager } from "@/components/LicenseManager";
import { HelpGuide } from "@/components/HelpGuide";
import { Settings } from "@/components/Settings";
import { LoginForm } from "@/components/LoginForm";
import { UserManagement } from "@/components/UserManagement";
import { EmployeeManagement } from "@/components/EmployeeManagement";
import { SignUpDashboard } from "@/components/SignUpDashboard";
import { DocumentManager } from "@/components/DocumentManager";
import { RequestForm } from "@/components/RequestForm";
import { RequestManager } from "@/components/RequestManager";
import { RegistrationManager } from "@/components/RegistrationManager";
import { authService } from "@/lib/authService";
import { dataService } from "@/lib/dataService";
import { toast, Toaster } from "sonner";
import type { Permissions } from "@/types/auth";
import { DEFAULT_ADMIN_PERMISSIONS } from "@/types/auth";
import type { FightCard } from "@/types/fightCard";
import type { Boxer, Sponsor, RankingSettings } from "@/types/boxer";
import type { Tournament } from "@/types/tournament";
import type { Bet, BettingPool, PayoutSettings } from "@/types/betting";
import type { FightRequest, SponsorRequest } from "@/types/request";
import { DEFAULT_RANKING_SETTINGS, calculatePointsForFight, getSortedBoxers } from "@/lib/rankingUtils";
import { settleBet, DEFAULT_PAYOUT_SETTINGS } from "@/lib/bettingUtils";
import { LICENSE_FEE } from "@/lib/licenseUtils";
import type { BettingConfig } from "@/types/betting";

// API client for backend communication

const DEFAULT_BETTING_CONFIG: BettingConfig = {
  id: 'default',
  enabled: true,
  eventPricing: {
    regular: 2000,
    special: 5000,
    tournament: 5000,
  },
  wageLimits: {
    minimum: 2000,
    maximum: 50000,
    perFight: 10000,
    perEvent: 50000,
  },
  lastUpdated: new Date().toISOString(),
};

/* ---------- default data ---------- */
const defaultFightCard: FightCard = {
  eventDate: '',
  location: '',
  mainEvent: {
    id: 'main-event',
    fighter1: '',
    fighter2: '',
    type: 'main',
  },
  otherBouts: [],
  sponsors: '',
  status: 'upcoming',
};

/* ---------- App component (main) ---------- */
function App() {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Initialize auth service
  useEffect(() => {
    authService.initialize();
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [isAdmin, setIsAdmin] = useState(authService.isAdmin());
  const currentUser = authService.getSession()?.user;
  const [userPermissions, setUserPermissions] = useState<Permissions>(DEFAULT_ADMIN_PERMISSIONS);
  
  // Role-based UI helpers
  const isBoxerOrSponsor = currentUser?.role === 'boxer' || currentUser?.role === 'sponsor';
  const isStaffOrAdmin = currentUser?.role === 'staff' || currentUser?.role === 'admin';

  // Load initial permissions
  useEffect(() => {
    const loadPermissions = async () => {
      if (isAuthenticated) {
        const perms = await authService.getUserPermissions();
        setUserPermissions(perms);
      }
    };
    loadPermissions();
  }, [isAuthenticated]);

  // Listen for permission updates and refresh immediately
  useEffect(() => {
    const handlePermissionsUpdate = async (event: Event) => {
      const customEvent = event as CustomEvent<{ username: string }>;
      const session = authService.getSession();
      // If the updated user is the current user, refresh their session
      if (session && customEvent.detail?.username === session.user.username) {
        // Re-fetch user data to get updated permissions
        const response = await fetch(`/api/auth/users`);
        if (response.ok) {
          const users = await response.json();
          const updatedUser = users.find((u: any) => u.username === session.user.username);
          if (updatedUser) {
            // Update session with new permissions
            const updatedSession = {
              ...session,
              user: {
                ...session.user,
                permissions: updatedUser.permissions,
              },
            };
            localStorage.setItem('lsba_auth_session', JSON.stringify(updatedSession));
            
            // Refresh permissions in state
            const perms = await authService.getUserPermissions();
            setUserPermissions(perms);
            toast.success('Your permissions have been updated!');
          }
        }
      }
    };

    window.addEventListener('permissionsUpdated', handlePermissionsUpdate);
    return () => {
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdate);
    };
  }, []);

  // Poll for permission updates every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      await authService.updateSession();
      // Refresh permissions and admin status
      const perms = await authService.getUserPermissions();
      setUserPermissions(perms);
      setIsAdmin(authService.isAdmin());
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // State hooks
  const [savedCard, setSavedCard] = useState<FightCard>(defaultFightCard);
  const [fightCards, setFightCards] = useState<FightCard[]>([]);
  const [boxers, setBoxers] = useState<Boxer[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [bettingConfig, setBettingConfig] = useState<BettingConfig>(DEFAULT_BETTING_CONFIG);
  const [rankingSettings, setRankingSettings] = useState<RankingSettings>(DEFAULT_RANKING_SETTINGS);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [bets, setBets] = useState<Bet[]>([]);
  const [bettingPools, setBettingPools] = useState<BettingPool[]>([]);
  const [payoutSettings, setPayoutSettings] = useState<PayoutSettings>(DEFAULT_PAYOUT_SETTINGS);
  const [fightRequests, setFightRequests] = useState<FightRequest[]>([]);
  const [sponsorRequests, setSponsorRequests] = useState<SponsorRequest[]>([]);
  const [editingCard, setEditingCard] = useState<FightCard>(defaultFightCard);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [selectedBoxer, setSelectedBoxer] = useState<Boxer | null>(null);
  const [selectedSponsor, setSelectedSponsor] = useState<Sponsor | null>(null);

  const boxersList = boxers ?? [];
  const sponsorsList = sponsors ?? [];
  const fightCardsList = fightCards ?? [];
  const tournamentsList = tournaments ?? [];
  const betsList = bets ?? [];
  const bettingPoolsList = bettingPools ?? [];
  const currentCard = savedCard ?? defaultFightCard;
  const currentSettings = rankingSettings ?? DEFAULT_RANKING_SETTINGS;

  /* ---------- On mount: load data from API/Redis ---------- */
  useEffect(() => {
    const loadData = async () => {
      // Load all data from API/Redis
      const apiBoxers = await dataService.get<Boxer[]>('lsba_boxers');
      const apiSponsors = await dataService.get<Sponsor[]>('lsba_sponsors');
      const apiFightCards = await dataService.get<FightCard[]>('lsba_fightCards');
      const apiBets = await dataService.get<Bet[]>('lsba_bets');
      const apiPools = await dataService.get<BettingPool[]>('lsba_bettingPools');
      const apiTournaments = await dataService.get<Tournament[]>('lsba_tournaments');
      const apiFightRequests = await dataService.get<FightRequest[]>('lsba_fightRequests');
      const apiSponsorRequests = await dataService.get<SponsorRequest[]>('lsba_sponsorRequests');
      
      if (apiBoxers && apiBoxers.length > 0) {
        console.log('Loaded', apiBoxers.length, 'boxers from API');
        setBoxers(apiBoxers);
      }
      
      if (apiSponsors && apiSponsors.length > 0) {
        console.log('Loaded', apiSponsors.length, 'sponsors from API');
        setSponsors(apiSponsors);
      }

      if (apiFightCards && apiFightCards.length > 0) {
        setFightCards(apiFightCards);
        console.log('Loaded', apiFightCards.length, 'fight cards from API');
      }

      if (apiBets && apiBets.length > 0) {
        setBets(apiBets);
        console.log('Loaded', apiBets.length, 'bets from API');
      }

      if (apiPools && apiPools.length > 0) {
        setBettingPools(apiPools);
        console.log('Loaded', apiPools.length, 'betting pools from API');
      }

      if (apiTournaments && apiTournaments.length > 0) {
        setTournaments(apiTournaments);
        console.log('Loaded', apiTournaments.length, 'tournaments from API');
      }

      if (apiFightRequests && apiFightRequests.length > 0) {
        setFightRequests(apiFightRequests);
        console.log('Loaded', apiFightRequests.length, 'fight requests from API');
      }

      if (apiSponsorRequests && apiSponsorRequests.length > 0) {
        setSponsorRequests(apiSponsorRequests);
        console.log('Loaded', apiSponsorRequests.length, 'sponsor requests from API');
      }
    };

    loadData();
    
    // Ensure existing behavior for saved card
    if (savedCard) setEditingCard(savedCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Migrations from original app (kept intact) ---------- */
  useEffect(() => {
    if (sponsors && sponsors.length > 0) {
      const needsMigration = sponsors.some(
        sponsor => !sponsor.boxersSponsored || !sponsor.additionalContacts
      );
      
      if (needsMigration) {
        const migratedSponsors = sponsors.map(sponsor => ({
          ...sponsor,
          boxersSponsored: sponsor.boxersSponsored || [],
          additionalContacts: sponsor.additionalContacts || [],
        }));
        setSponsors(migratedSponsors);
      }
    }
  }, [sponsors, setSponsors]);

  useEffect(() => {
    if (boxers && boxers.length > 0) {
      const needsTimezoneMigration = boxers.some(boxer => !boxer.timezone);
      const needsLicenseMigration = boxers.some(boxer => 
        boxer.licenseStatus === undefined || 
        boxer.lastPaymentDate === undefined || 
        boxer.licenseFee === undefined
      );
      const needsNewFieldsMigration = boxers.some(boxer =>
        boxer.feePaid === undefined ||
        (boxer.licenseStatus === 'suspended' && !boxer.suspensionReason)
      );
      
      if (needsTimezoneMigration || needsLicenseMigration || needsNewFieldsMigration) {
        const migratedBoxers = boxers.map(boxer => ({
          ...boxer,
          timezone: boxer.timezone || 'NA',
          licenseStatus: boxer.licenseStatus || 'active',
          lastPaymentDate: boxer.lastPaymentDate || boxer.registeredDate || new Date().toISOString(),
          licenseFee: boxer.licenseFee || LICENSE_FEE,
          feePaid: boxer.feePaid !== undefined ? boxer.feePaid : true,
        }));
        setBoxers(migratedBoxers);
      }
    }
  }, [boxers, setBoxers]);

  /* ---------- Auto-save data to API when changed ---------- */
  useEffect(() => {
    if (boxers.length > 0) {
      dataService.set('lsba_boxers', boxers);
    }
  }, [boxers]);

  useEffect(() => {
    if (sponsors.length > 0) {
      dataService.set('lsba_sponsors', sponsors);
    }
  }, [sponsors]);

  useEffect(() => {
    if (fightCards.length > 0) {
      dataService.set('lsba_fightCards', fightCards);
    }
  }, [fightCards]);

  useEffect(() => {
    if (bets.length > 0) {
      dataService.set('lsba_bets', bets);
    }
  }, [bets]);

  useEffect(() => {
    if (bettingPools.length > 0) {
      dataService.set('lsba_bettingPools', bettingPools);
    }
  }, [bettingPools]);

  useEffect(() => {
    if (tournaments.length > 0) {
      dataService.set('lsba_tournaments', tournaments);
    }
  }, [tournaments]);

  useEffect(() => {
    dataService.set('lsba_fightRequests', fightRequests);
  }, [fightRequests]);

  useEffect(() => {
    dataService.set('lsba_sponsorRequests', sponsorRequests);
  }, [sponsorRequests]);



  /* ---------- App handlers (kept original behaviour, plus DB writes) ---------- */

  const handleSave = () => {
    setSavedCard(editingCard);
    toast.success('Fight card saved successfully!');
  };

  // Register boxer locally
  const handleRegisterBoxer = async (boxer: Boxer) => {
    setBoxers((current) => [...(current || []), boxer]);
    
    if (boxer.sponsor) {
      setSponsors((current) => {
        const updated = [...(current || [])];
        const sponsorIndex = updated.findIndex(s => s.name.toLowerCase() === boxer.sponsor.toLowerCase());
        
        if (sponsorIndex !== -1) {
          const currentSponsored = updated[sponsorIndex].boxersSponsored || [];
          updated[sponsorIndex].boxersSponsored = [
            ...currentSponsored,
            boxer.id
          ];
        }
        
        return updated;
      });
    }

    toast.success(`${boxer.firstName} ${boxer.lastName} registered successfully`);
  };

  // Register sponsor locally
  const handleRegisterSponsor = async (sponsor: Sponsor) => {
    setSponsors((current) => [...(current || []), sponsor]);
    toast.success('Sponsor registered successfully');
  };

  // Update boxer locally
  const handleUpdateBoxer = async (updatedBoxer: Boxer) => {
    const oldBoxer = boxersList.find(b => b.id === updatedBoxer.id);
    
    setBoxers((current) =>
      (current || []).map((b) => (b.id === updatedBoxer.id ? updatedBoxer : b))
    );
    setSelectedBoxer(updatedBoxer);
    
    if (oldBoxer && oldBoxer.sponsor !== updatedBoxer.sponsor) {
      setSponsors((current) => {
        const updated = [...(current || [])];
        
        if (oldBoxer.sponsor) {
          const oldSponsorIndex = updated.findIndex(s => s.name.toLowerCase() === oldBoxer.sponsor.toLowerCase());
          if (oldSponsorIndex !== -1) {
            const currentSponsored = updated[oldSponsorIndex].boxersSponsored || [];
            updated[oldSponsorIndex].boxersSponsored = currentSponsored.filter(
              id => id !== updatedBoxer.id
            );
          }
        }
        
        if (updatedBoxer.sponsor) {
          const newSponsorIndex = updated.findIndex(s => s.name.toLowerCase() === updatedBoxer.sponsor.toLowerCase());
          if (newSponsorIndex !== -1) {
            const currentSponsored = updated[newSponsorIndex].boxersSponsored || [];
            if (!currentSponsored.includes(updatedBoxer.id)) {
              updated[newSponsorIndex].boxersSponsored = [
                ...currentSponsored,
                updatedBoxer.id
              ];
            }
          }
        }
        
        return updated;
      });
    }

    toast.success('Boxer updated successfully');
  };

  // Delete boxer locally
  const handleDeleteBoxer = async (boxerId: string) => {
    const boxer = boxersList.find(b => b.id === boxerId);
    
    setBoxers((current) => (current || []).filter((b) => b.id !== boxerId));
    
    if (boxer?.sponsor) {
      setSponsors((current) => {
        const updated = [...(current || [])];
        const sponsorIndex = updated.findIndex(s => s.name.toLowerCase() === boxer.sponsor.toLowerCase());
        
        if (sponsorIndex !== -1) {
          const currentSponsored = updated[sponsorIndex].boxersSponsored || [];
          updated[sponsorIndex].boxersSponsored = currentSponsored.filter(
            id => id !== boxerId
          );
        }
        
        return updated;
      });
    }
    
    toast.success("Fighter profile deleted successfully");
  };

  // Generate fight card locally
  const handleGenerateFightCard = async (fightCard: FightCard, boxerIds: string[]) => {
    setEditingCard(fightCard);
    setSavedCard(fightCard);
    
    setFightCards((current) => [...(current || []), fightCard]);
    
    const eventName = fightCard.mainEvent.title || `LSBA Event - ${fightCard.eventDate}`;
    const eventDate = fightCard.createdDate || new Date().toISOString();
    
    const matchedPairs: Array<[string, string]> = [];
    for (let i = 0; i < boxerIds.length; i += 2) {
      if (i + 1 < boxerIds.length) {
        matchedPairs.push([boxerIds[i], boxerIds[i + 1]]);
      }
    }
    
    setBoxers((current) => {
      const updated = [...(current || [])];
      matchedPairs.forEach(([fighterId1, fighterId2]) => {
        const boxer1Index = updated.findIndex(b => b.id === fighterId1);
        const boxer2Index = updated.findIndex(b => b.id === fighterId2);
        
        if (boxer1Index !== -1) {
          const opponent = updated[boxer2Index];
          updated[boxer1Index].fightHistory = [
            {
              id: `fight-${Date.now()}-${fighterId1}`,
              opponent: opponent ? `${opponent.firstName} ${opponent.lastName}` : 'TBD',
              date: eventDate,
              result: 'pending',
              eventName,
              fightCardId: fightCard.id,
            },
            ...updated[boxer1Index].fightHistory,
          ];
        }
        
        if (boxer2Index !== -1) {
          const opponent = updated[boxer1Index];
          updated[boxer2Index].fightHistory = [
            {
              id: `fight-${Date.now()}-${fighterId2}`,
              opponent: opponent ? `${opponent.firstName} ${opponent.lastName}` : 'TBD',
              date: eventDate,
              result: 'pending',
              eventName,
              fightCardId: fightCard.id,
            },
            ...updated[boxer2Index].fightHistory,
          ];
        }
      });
      return updated;
    });
    
    setActiveTab('upcoming-fights');
    toast.success('Fight card generated! View upcoming fights.');


  };

  // Declare results and update boxers locally and persist changed boxers
  const handleDeclareResults = async (updatedCard: FightCard, boxerUpdates: Map<string, Partial<Boxer>>) => {
    setFightCards((current) =>
      (current || []).map((card) => (card.id === updatedCard.id ? updatedCard : card))
    );

    setBoxers((current) => {
      const updated = [...(current || [])];
      
      const allBouts = [
        updatedCard.mainEvent,
        ...(updatedCard.coMainEvent ? [updatedCard.coMainEvent] : []),
        ...updatedCard.otherBouts,
      ];

      allBouts.forEach((bout) => {
        if (bout.winner && bout.fighter1Id && bout.fighter2Id) {
          const winnerIndex = updated.findIndex((b) => 
            b.id === (bout.winner === 'fighter1' ? bout.fighter1Id : bout.fighter2Id)
          );
          const loserIndex = updated.findIndex((b) => 
            b.id === (bout.winner === 'fighter1' ? bout.fighter2Id : bout.fighter1Id)
          );

          if (winnerIndex !== -1 && loserIndex !== -1) {
            const winner = updated[winnerIndex];
            const loser = updated[loserIndex];
            const knockout = bout.knockout || false;

            const { winnerPoints, loserPoints } = calculatePointsForFight(
              winner,
              loser,
              knockout,
              updated,
              currentSettings
            );

            updated[winnerIndex].rankingPoints += winnerPoints;
            updated[loserIndex].rankingPoints = Math.max(0, updated[loserIndex].rankingPoints + loserPoints);

            updated[winnerIndex].fightHistory = updated[winnerIndex].fightHistory.map((fight) => {
              if (fight.fightCardId === updatedCard.id && fight.result === 'pending') {
                return {
                  ...fight,
                  result: knockout ? 'knockout' : 'win',
                  pointsChange: winnerPoints,
                };
              }
              return fight;
            });

            updated[loserIndex].fightHistory = updated[loserIndex].fightHistory.map((fight) => {
              if (fight.fightCardId === updatedCard.id && fight.result === 'pending') {
                return {
                  ...fight,
                  result: 'loss',
                  pointsChange: loserPoints,
                };
              }
              return fight;
            });
          }
        }
      });

      boxerUpdates.forEach((updates, boxerId) => {
        const index = updated.findIndex((b) => b.id === boxerId);
        if (index !== -1) {
          updated[index] = {
            ...updated[index],
            wins: updates.wins ?? updated[index].wins,
            losses: updates.losses ?? updated[index].losses,
            knockouts: updates.knockouts ?? updated[index].knockouts,
          };
        }
      });

      return updated;
    });



    toast.success('Fight results declared! Rankings updated. Bets remain pending for manual settlement.');
  };

  const handleResetSeason = () => {
    setBoxers((current) =>
      (current || []).map((boxer) => ({
        ...boxer,
        rankingPoints: 0,
        fightHistory: [],
      }))
    );
    toast.success('Season reset! All ranking points and fight history cleared.');
  };

  const handleClearAll = () => {
    setBoxers([]);
    setFightCards([]);
    setSavedCard(defaultFightCard);
    setEditingCard(defaultFightCard);
    toast.success('All data cleared. Starting fresh!');
  };

  const handleSaveSettings = (settings: RankingSettings) => {
    setRankingSettings(settings);
  };

  const handleUpdateSponsor = async (updatedSponsor: Sponsor) => {
    setSponsors((current) =>
      (current || []).map((s) => (s.id === updatedSponsor.id ? updatedSponsor : s))
    );
    setSelectedSponsor(updatedSponsor);
    toast.success('Sponsor updated successfully!');
  };

  const handleCreateTournament = (tournament: Tournament) => {
    setTournaments((current) => [...(current || []), tournament]);
  };

  const handleUpdateTournament = (tournament: Tournament) => {
    setTournaments((current) =>
      (current || []).map((t) => (t.id === tournament.id ? tournament : t))
    );
  };

  const handleDeleteTournament = (tournamentId: string) => {
    setTournaments((current) => (current || []).filter((t) => t.id !== tournamentId));
    toast.success('Tournament deleted successfully');
  };

  const handlePlaceBet = (bet: Bet) => {
    setBets((current) => [...(current || []), bet]);
  };

  const handleUpdatePool = (pool: BettingPool) => {
    setBettingPools((current) => {
      const existing = (current || []).find(p => p.fightCardId === pool.fightCardId);
      if (existing) {
        return (current || []).map(p => p.fightCardId === pool.fightCardId ? pool : p);
      }
      return [...(current || []), pool];
    });
  };

  const handleSettleBets = (settledBets: Array<{ bet: Bet; winnerId: string; note: string }>) => {
    setBets((currentBets) => {
      const updatedBets = [...(currentBets || [])];
      
      settledBets.forEach(({ bet, winnerId, note }) => {
        const index = updatedBets.findIndex(b => b.id === bet.id);
        if (index !== -1) {
          updatedBets[index] = settleBet(bet, winnerId, note, payoutSettings || DEFAULT_PAYOUT_SETTINGS);
        }
      });
      
      return updatedBets;
    });
  };

  // Request handlers
  const handleSubmitFightRequest = (request: FightRequest) => {
    setFightRequests((current) => [...(current || []), request]);
    toast.success('Fight request submitted successfully');
  };

  const handleSubmitSponsorRequest = (request: SponsorRequest) => {
    setSponsorRequests((current) => [...(current || []), request]);
    toast.success('Sponsorship request submitted successfully');
  };

  const handleApproveFightRequest = (requestId: string, reviewNotes?: string) => {
    setFightRequests((current) =>
      (current || []).map((r) =>
        r.id === requestId
          ? {
              ...r,
              status: 'approved' as const,
              reviewedAt: new Date().toISOString(),
              reviewNotes,
            }
          : r
      )
    );
    toast.success('Fight request approved');
  };

  const handleRejectFightRequest = (requestId: string, reviewNotes?: string) => {
    setFightRequests((current) =>
      (current || []).map((r) =>
        r.id === requestId
          ? {
              ...r,
              status: 'rejected' as const,
              reviewedAt: new Date().toISOString(),
              reviewNotes,
            }
          : r
      )
    );
    toast.success('Fight request rejected');
  };

  const handleApproveSponsorRequest = (requestId: string, reviewNotes?: string) => {
    setSponsorRequests((current) =>
      (current || []).map((r) =>
        r.id === requestId
          ? {
              ...r,
              status: 'approved' as const,
              reviewedAt: new Date().toISOString(),
              reviewNotes,
            }
          : r
      )
    );
    toast.success('Sponsorship request approved');
  };

  const handleRejectSponsorRequest = (requestId: string, reviewNotes?: string) => {
    setSponsorRequests((current) =>
      (current || []).map((r) =>
        r.id === requestId
          ? {
              ...r,
              status: 'rejected' as const,
              reviewedAt: new Date().toISOString(),
              reviewNotes,
            }
          : r
      )
    );
    toast.success('Sponsorship request rejected');
  };

  const hasChanges = JSON.stringify(currentCard) !== JSON.stringify(editingCard);

  // Authentication handlers
  const handleLoginSuccess = async () => {
    setIsAuthenticated(true);
    setIsAdmin(authService.isAdmin());
    const perms = await authService.getUserPermissions();
    setUserPermissions(perms);
    
    // Set default tab based on role
    const session = authService.getSession();
    if (session?.user.role === 'boxer' || session?.user.role === 'sponsor') {
      setActiveTab('leaderboard'); // Boxer/Sponsor see leaderboard first
    } else {
      setActiveTab('dashboard'); // Staff/Admin see dashboard first
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setIsAdmin(false);
    toast.success('Logged out successfully');
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <LoginForm onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  /* ---------- Render logic (kept exactly as original) ---------- */

  if (selectedSponsor && !isBoxerOrSponsor) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
              <SponsorProfile
                sponsor={selectedSponsor}
                boxers={boxersList}
                onBack={() => setSelectedSponsor(null)}
                onUpdateSponsor={handleUpdateSponsor}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (selectedBoxer && !isBoxerOrSponsor) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto space-y-6">
              <BoxerProfile
                boxer={selectedBoxer}
                allBoxers={boxersList}
                onBack={() => setSelectedBoxer(null)}
                onUpdateBoxer={handleUpdateBoxer}
                userPermissions={userPermissions}
              />
              <FighterOddsDisplay
                boxer={selectedBoxer}
                bettingPools={bettingPoolsList}
                fightCards={fightCardsList}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase text-transparent bg-clip-text bg-gradient-to-r from-secondary via-accent to-secondary tracking-wide">
                    LSBA Manager
                  </h1>
                  <p className="text-muted-foreground mt-2 text-sm sm:text-base md:text-lg">
                    Los Santos Boxing Association - Complete Management System
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground hidden sm:inline">
                    {currentUser?.username} ({currentUser?.role})
                  </span>
                  <Button variant="outline" size="sm" onClick={handleLogout}>
                    Sign Out
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-card border border-border rounded-lg p-2">
                <TabsList className="flex-1 bg-transparent h-auto grid grid-cols-2 sm:flex sm:flex-row gap-1">
                  {/* Show Dashboard only for staff/admin */}
                  {isStaffOrAdmin && (
                    <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:bg-accent">
                      <SquaresFour className="w-4 h-4" />
                      <span className="hidden md:inline">Dashboard</span>
                    </TabsTrigger>
                  )}
                  
                  {/* Show Leaderboard for all roles */}
                  <TabsTrigger value="leaderboard" className="flex items-center gap-2 data-[state=active]:bg-accent">
                    <ChartLine className="w-4 h-4" />
                    <span className="hidden md:inline">Leaderboard</span>
                  </TabsTrigger>
                  
                  {/* Show Directory for all roles */}
                  <TabsTrigger value="directory" className="flex items-center gap-2 data-[state=active]:bg-accent">
                    <AddressBook className="w-4 h-4" />
                    <span className="hidden md:inline">Directory</span>
                  </TabsTrigger>
                  
                  <TabsTrigger value="upcoming-fights" className="flex items-center gap-2 data-[state=active]:bg-accent">
                    <Calendar className="w-4 h-4" />
                    <span className="hidden md:inline">Upcoming</span>
                  </TabsTrigger>
                  
                  {/* Show Licenses/Betting only for staff/admin */}
                  {isStaffOrAdmin && (
                    <>
                      <TabsTrigger value="licenses" className="flex items-center gap-2 data-[state=active]:bg-accent">
                        <IdentificationCard className="w-4 h-4" />
                        <span className="hidden md:inline">Licenses</span>
                      </TabsTrigger>
                      <TabsTrigger value="betting" className="flex items-center gap-2 data-[state=active]:bg-accent">
                        <CurrencyDollar className="w-4 h-4" />
                        <span className="hidden md:inline">Betting</span>
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>

                <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto">
                  {/* Fighters dropdown - only for staff/admin */}
                  {isStaffOrAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1 sm:gap-2 flex-1 sm:flex-none px-2 sm:px-3 text-xs sm:text-sm">
                          <AddressBook className="w-4 h-4" />
                          <span className="hidden sm:inline">Fighters</span>
                          <CaretDown className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => setActiveTab('directory')}>
                          <AddressBook className="w-4 h-4 mr-2" />
                          Directory
                        </DropdownMenuItem>
                        {userPermissions.registerBoxers && (
                          <DropdownMenuItem onClick={() => setActiveTab('register')}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Register Fighter
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setActiveTab('sponsors')}>
                          <Briefcase className="w-4 h-4 mr-2" />
                          Sponsors
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* Events dropdown - accessible to all, but limited options for boxer/sponsor */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1 sm:gap-2 flex-1 sm:flex-none px-2 sm:px-3 text-xs sm:text-sm">
                        <Sparkle className="w-4 h-4" />
                        <span className="hidden sm:inline">Events</span>
                        <CaretDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {/* Boxer/Sponsor can view fight cards */}
                      {isBoxerOrSponsor && (
                        <DropdownMenuItem onClick={() => setActiveTab('fight-card')}>
                          <Calendar className="w-4 h-4 mr-2" />
                          Fight Cards
                        </DropdownMenuItem>
                      )}
                      
                      {/* Staff/Admin get full access */}
                      {isStaffOrAdmin && (
                        <>
                          {userPermissions.generateFightCards && (
                            <DropdownMenuItem onClick={() => setActiveTab('generator')}>
                              <Sparkle className="w-4 h-4 mr-2" />
                              Card Generator
                            </DropdownMenuItem>
                          )}
                          {userPermissions.editFightCards && (
                            <DropdownMenuItem onClick={() => setActiveTab('fight-card')}>
                              <PencilSimple className="w-4 h-4 mr-2" />
                              Fight Card Editor
                            </DropdownMenuItem>
                          )}
                          {userPermissions.manageTournaments && (
                            <DropdownMenuItem onClick={() => setActiveTab('tournament')}>
                              <Trophy className="w-4 h-4 mr-2" />
                              Tournament
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* More dropdown - for all users but with role-specific items */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1 sm:gap-2 flex-1 sm:flex-none px-2 sm:px-3 text-xs sm:text-sm">
                        <Sliders className="w-4 h-4" />
                        <span className="hidden sm:inline">More</span>
                        <CaretDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => setActiveTab('help')}>
                        <Book className="w-4 h-4 mr-2" />
                        Help Guide
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setActiveTab('faq')}>
                        <Info className="w-4 h-4 mr-2" />
                        FAQ
                      </DropdownMenuItem>
                      
                      {/* Boxer only: Request a Fight */}
                      {currentUser.role === 'boxer' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setActiveTab('requests')}>
                            <HandsClapping className="w-4 h-4 mr-2" />
                            Request a Fight
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {/* Staff/Admin only items */}
                      {isStaffOrAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                            <Sliders className="w-4 h-4 mr-2" />
                            Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActiveTab('request-management')}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Request Management
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setActiveTab('users')}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            User Management
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActiveTab('registrations')}>
                            <IdentificationCard className="w-4 h-4 mr-2" />
                            Registrations
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActiveTab('employees')}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Employee Management
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActiveTab('signup-stats')}>
                            <ChartLine className="w-4 h-4 mr-2" />
                            Sign-Up Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActiveTab('season')}>
                            <ArrowsClockwise className="w-4 h-4 mr-2" />
                            Season Reset
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {(isAdmin || currentUser?.role === 'legal-team') && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setActiveTab('documents')}>
                            <FileText className="w-4 h-4 mr-2" />
                            Document Management
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <TabsContent value="dashboard" className="mt-4 sm:mt-6">
                <div className="flex flex-col gap-4 sm:gap-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-lg p-4 sm:p-6">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <ChartLine className="w-6 h-6 sm:w-8 sm:h-8 text-primary" weight="bold" />
                        <div className="text-3xl sm:text-4xl font-display font-bold text-primary">
                          {getSortedBoxers(boxersList).length}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm uppercase tracking-wide text-muted-foreground">
                        Ranked Fighters
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-secondary/20 to-secondary/5 border border-secondary/30 rounded-lg p-4 sm:p-6">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-secondary" weight="bold" />
                        <div className="text-3xl sm:text-4xl font-display font-bold text-secondary">
                          {fightCardsList.filter(card => card.status === 'upcoming').length}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm uppercase tracking-wide text-muted-foreground">
                        Upcoming Events
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/30 rounded-lg p-4 sm:p-6">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <Sparkle className="w-6 h-6 sm:w-8 sm:h-8 text-accent" weight="fill" />
                        <div className="text-3xl sm:text-4xl font-display font-bold text-accent">
                          {boxersList.length}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm uppercase tracking-wide text-muted-foreground">
                        Total Registered
                      </div>
                    </div>
                  </div>

                  <UpcomingFights fightCards={fightCardsList} boxers={boxersList} />

                  <BoxerLeaderboard boxers={boxersList} onSelectBoxer={setSelectedBoxer} />
                </div>
              </TabsContent>

              <TabsContent value="licenses" className="mt-4 sm:mt-6">
                <LicenseManager
                  boxers={boxersList}
                  onUpdateBoxer={handleUpdateBoxer}
                />
              </TabsContent>

              <TabsContent value="betting" className="mt-4 sm:mt-6">
                <BettingManager
                  fightCards={fightCardsList}
                  boxers={boxersList}
                  bets={betsList}
                  bettingPools={bettingPoolsList}
                  onPlaceBet={handlePlaceBet}
                  onUpdatePool={handleUpdatePool}
                  onSettleBets={handleSettleBets}
                  payoutSettings={payoutSettings}
                  setPayoutSettings={setPayoutSettings}
                  bettingConfig={bettingConfig}
                  setBettingConfig={setBettingConfig}
                />
              </TabsContent>

              <TabsContent value="upcoming-fights" className="mt-4 sm:mt-6">
                <div className="flex flex-col gap-4 sm:gap-6">
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <div>
                      <h2 className="font-display text-2xl sm:text-3xl uppercase text-secondary tracking-wide">
                        Upcoming Fights
                      </h2>
                      <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                        Scheduled bouts awaiting results
                      </p>
                    </div>
                  </div>

                  <UpcomingFights fightCards={fightCardsList} boxers={boxersList} />

                  {/* Only show Declare Results section to staff/admin */}
                  {isStaffOrAdmin && fightCardsList.filter(card => card.status === 'upcoming').length > 0 && (
                    <>
                      <div className="mt-6 sm:mt-8">
                        <h3 className="font-display text-xl sm:text-2xl uppercase text-secondary tracking-wide mb-3 sm:mb-4">
                          Declare Results
                        </h3>
                        <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base">
                          Select a fight card below to declare winners and update fighter records
                        </p>
                      </div>
                      {fightCardsList.filter(card => card.status === 'upcoming').map((card) => (
                        <FightResultsManager
                          key={card.id}
                          fightCard={card}
                          boxers={boxersList}
                          onDeclareResults={handleDeclareResults}
                        />
                      ))}
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="tournament" className="mt-4 sm:mt-6">
                <TournamentBracket
                  boxers={boxersList}
                  tournaments={tournamentsList}
                  onCreateTournament={handleCreateTournament}
                  onUpdateTournament={handleUpdateTournament}
                  onDeleteTournament={handleDeleteTournament}
                />
              </TabsContent>

              <TabsContent value="leaderboard" className="mt-4 sm:mt-6">
                <BoxerLeaderboard boxers={boxersList} onSelectBoxer={setSelectedBoxer} />
              </TabsContent>

              <TabsContent value="directory" className="mt-4 sm:mt-6">
                <BoxerDirectory 
                  boxers={boxersList} 
                  sponsors={sponsorsList}
                  onUpdateBoxer={handleUpdateBoxer}
                  onDeleteBoxer={handleDeleteBoxer}
                  onViewProfile={setSelectedBoxer}
                  onViewSponsorProfile={setSelectedSponsor}
                  hideStateId={isBoxerOrSponsor}
                  userPermissions={userPermissions}
                />
              </TabsContent>

              <TabsContent value="register" className="mt-4 sm:mt-6">
                {userPermissions.registerBoxers ? (
                  <BoxerRegistration 
                    onRegister={handleRegisterBoxer} 
                    existingBoxers={boxersList}
                    existingSponsors={sponsorsList}
                  />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to register boxers
                  </div>
                )}
              </TabsContent>

              <TabsContent value="generator" className="mt-4 sm:mt-6">
                {userPermissions.generateFightCards ? (
                  <FightCardGenerator boxers={boxersList} allBoxers={boxersList} onGenerate={handleGenerateFightCard} />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to generate fight cards
                  </div>
                )}
              </TabsContent>

              <TabsContent value="sponsors" className="mt-4 sm:mt-6">
                {userPermissions.viewSponsors ? (
                  <div className="flex flex-col gap-4 sm:gap-6">
                    {userPermissions.registerSponsors && (
                      <SponsorRegistration 
                        onRegister={handleRegisterSponsor} 
                        existingSponsors={sponsorsList}
                      />
                    )}
                    <SponsorList 
                      sponsors={sponsorsList} 
                      onViewProfile={setSelectedSponsor}
                    />
                  </div>
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to view sponsors
                  </div>
                )}
              </TabsContent>

              <TabsContent value="fight-card" className="mt-4 sm:mt-6">
                {/* Boxer/Sponsor: View-only mode */}
                {isBoxerOrSponsor ? (
                  <div className="flex flex-col gap-4 sm:gap-6">
                    <div>
                      <h2 className="font-display text-2xl sm:text-3xl uppercase text-secondary tracking-wide">
                        Fight Cards
                      </h2>
                      <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                        View upcoming and past LSBA boxing events
                      </p>
                    </div>
                    
                    <div className="flex justify-center">
                      <FightCardDisplay fightCard={currentCard} />
                    </div>
                  </div>
                ) : userPermissions.editFightCards ? (
                  /* Staff/Admin: Full editor mode */
                  <div className="flex flex-col gap-4 sm:gap-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
                    <div>
                      <h2 className="font-display text-2xl sm:text-3xl uppercase text-secondary tracking-wide">
                        Fight Card Editor
                      </h2>
                      <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                        Create and manage your LSBA boxing events
                      </p>
                    </div>
                    
                    <Button
                      onClick={handleSave}
                      disabled={!hasChanges}
                      size="lg"
                      className="bg-primary hover:bg-primary/90 w-full md:w-auto"
                    >
                      <FloppyDisk className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Save Changes
                    </Button>
                  </div>

                  <Tabs defaultValue="edit" className="w-full">
                    <TabsList className="grid w-full max-w-md grid-cols-2">
                      <TabsTrigger value="edit" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <PencilSimple className="w-3 h-3 sm:w-4 sm:h-4" />
                        Edit Card
                      </TabsTrigger>
                      <TabsTrigger value="preview" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                        Preview
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="edit" className="mt-4 sm:mt-6">
                      <FightCardEditor
                        fightCard={editingCard}
                        onChange={setEditingCard}
                      />
                    </TabsContent>

                    <TabsContent value="preview" className="mt-4 sm:mt-6">
                      <div className="flex flex-col gap-3 sm:gap-4">
                        {hasChanges && (
                          <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 sm:p-4 text-center">
                            <p className="text-xs sm:text-sm text-accent-foreground">
                              You have unsaved changes. Save to update the preview with your latest edits.
                            </p>
                          </div>
                        )}
                        
                        <div className="flex justify-center">
                          <FightCardDisplay fightCard={currentCard} />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to edit fight cards
                  </div>
                )}
              </TabsContent>

              <TabsContent value="users" className="mt-4 sm:mt-6">
                {userPermissions.manageUsers ? (
                  <UserManagement />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to manage users
                  </div>
                )}
              </TabsContent>

              <TabsContent value="registrations" className="mt-4 sm:mt-6">
                {userPermissions.manageUsers ? (
                  <RegistrationManager currentAdminUsername={currentUser?.username || 'admin'} />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to manage registrations
                  </div>
                )}
              </TabsContent>

              <TabsContent value="employees" className="mt-4 sm:mt-6">
                {userPermissions.manageEmployees ? (
                  <EmployeeManagement />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to manage employees
                  </div>
                )}
              </TabsContent>

              <TabsContent value="signup-stats" className="mt-4 sm:mt-6">
                {userPermissions.viewSignUpStats ? (
                  <SignUpDashboard />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to view sign-up statistics
                  </div>
                )}
              </TabsContent>

              <TabsContent value="documents" className="mt-4 sm:mt-6">
                {userPermissions.viewDocuments || userPermissions.manageDocuments ? (
                  <DocumentManager />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to view documents
                  </div>
                )}
              </TabsContent>

              <TabsContent value="help" className="mt-4 sm:mt-6">
                <HelpGuide />
              </TabsContent>

              <TabsContent value="faq" className="mt-4 sm:mt-6">
                <RankingFAQ settings={currentSettings} />
              </TabsContent>

              <TabsContent value="settings" className="mt-4 sm:mt-6">
                <Settings 
                  currentData={{
                    boxers: boxersList,
                    sponsors: sponsorsList,
                    fightCards: fightCardsList,
                    tournaments: tournamentsList,
                    bets: betsList,
                    bettingPools: bettingPoolsList,
                    payoutSettings: payoutSettings
                  }}
                />
              </TabsContent>

              <TabsContent value="season" className="mt-4 sm:mt-6">
                {userPermissions.resetSeason ? (
                  <SeasonReset 
                    boxers={boxersList} 
                    onResetSeason={handleResetSeason}
                    onClearAll={handleClearAll}
                  />
                ) : (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground text-sm sm:text-base">
                    You don't have permission to reset the season
                  </div>
                )}
              </TabsContent>

              {/* Requests tab - for Boxer/Sponsor roles */}
              <TabsContent value="requests" className="mt-4 sm:mt-6">
                <RequestForm
                  currentUser={currentUser}
                  boxers={boxersList}
                  sponsors={sponsorsList}
                  allBoxers={boxersList}
                  onSubmitFightRequest={handleSubmitFightRequest}
                  onSubmitSponsorRequest={handleSubmitSponsorRequest}
                />
              </TabsContent>

              {/* Request Management tab - for Staff/Admin roles */}
              <TabsContent value="request-management" className="mt-4 sm:mt-6">
                <RequestManager
                  fightRequests={fightRequests}
                  sponsorRequests={sponsorRequests}
                  onApproveFightRequest={handleApproveFightRequest}
                  onRejectFightRequest={handleRejectFightRequest}
                  onApproveSponsorRequest={handleApproveSponsorRequest}
                  onRejectSponsorRequest={handleRejectSponsorRequest}
                />
              </TabsContent>
            </Tabs>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;